
import { scaleOrdinal } from 'd3-scale'
import { zoom as d3Zoom, select as d3Select, group as d3Group } from 'd3'
import { schemeTableau10 } from 'd3-scale-chromatic'
import { useMemo, useState, useCallback, useEffect, useContext } from 'react'
import { sankey as d3sankey, sankeyLeft, sankeyRight } from "d3-sankey";

import { SankeyContext } from './SankeyD3/context'
import { calculeTooltipPosition } from './formatters';

export const useZoom = (containerId, width, height) => {
  const svg = d3Select(`#${containerId} svg`)
  const g = svg.select("g.root");
  useEffect(() => {
    const zooming = d3Zoom()
    .scaleExtent([1, 8])
    .on("zoom", ({ transform: { k, x, y }, transform }) => {
        const widthLimit = (width * k) - width;
        const heightLimit = (height * k) - height;
        transform.x = k == 1 || x > 0 ? 0 : x < -widthLimit ? -widthLimit : x;
        transform.y = k == 1 || y > 0 ? 0 : y < -heightLimit ? -heightLimit : y;
        return g.attr("transform", transform)
      });

    svg.call(zooming);
  }, [svg.node()])
}

export const useColor = (items, field) => {
  const { size } = useMemo(() => d3Group(items, field), [items, items.length, field])
  const setColor = useMemo(() => scaleOrdinal().range(schemeTableau10), [size])

  return useCallback(node => setColor(field(node)), [setColor, field])
}

export function useSankeyColor(nodes) {
  const getNodeColor = useColor(nodes, d => d.page ? d.page : d.profile)
  const getLinkColor = useColor(nodes, d => d.pathWay)

  return useCallback((element, link = false) => {
    if (link) return getLinkColor(element.target);

    if (!element.color) {
      element.color = element.page === null ? '#737373' : element.page === 0 ? '#000' : getNodeColor(element);
    }
    return element.color
  }, [getNodeColor, getLinkColor])
}

export const useTooltip = () => {
  const { currentNode, currentLink, mosuePosition } = useSankeyContext()
  return { currentNode, currentLink, mosuePosition };
}

export const useNodeAndLinks = () => {
  const { setCurrentLink, setCurrentNode, setMousePosition, currentActives, setCurrentActives } = useSankeyContext()

  const opacities = {
    nodeOpacity: 1,
    nodeHoverOthersOpacity: 0.5,
    linkOpacity: 0.5,
    linkHoverOthersOpacity: 0.1,
  }
  const NODE_TOOLTIP_WIDTH = 360;
  const LINK_TOOLTIP_WIDTH = 420;
  const NODE_TOOLTIP_HEIGHT = 120;
  const LINK_TOOLTIP_HEIGHT = 140;
  const profileTooltipHeight = 80;

  const handleMouseEnter = useCallback((event, element, sankeyWidth, sankeyHeight) => {
    if (element.id) {
      setCurrentNode(element);
      const links = [];
      const targets = element.targetLinks.map(node => {
        links.push(`${node.source.id}${node.target.id}`)
        return node.source.id;
      }, {});

      const sources = element.sourceLinks.map(node => {
        links.push(`${node.source.id}${node.target.id}`)
        return node.target.id
      }, {});

      setCurrentActives([
        element.id,
        ...targets,
        ...sources,
        ...links,
      ]);

      setMousePosition(calculeTooltipPosition(event.nativeEvent.offsetX, event.nativeEvent.offsetY, sankeyWidth, sankeyHeight, NODE_TOOLTIP_WIDTH, element.page ? NODE_TOOLTIP_HEIGHT : profileTooltipHeight));
    } else {
      setCurrentLink(element);
      setCurrentActives([
        `${element.source.id}${element.target.id}`,
        element.target.id,
        element.source.id,
      ])
      setMousePosition(calculeTooltipPosition(event.nativeEvent.offsetX, event.nativeEvent.offsetY, sankeyWidth, sankeyHeight, LINK_TOOLTIP_WIDTH, LINK_TOOLTIP_HEIGHT));
    }
  }, []);

  const handleMouseMove = useCallback((event, element, sankeyWidth, sankeyHeight) => {
    const tooltipHeight = element.id ? NODE_TOOLTIP_HEIGHT : LINK_TOOLTIP_HEIGHT;
    setMousePosition(calculeTooltipPosition(event.nativeEvent.offsetX, event.nativeEvent.offsetY, sankeyWidth, sankeyHeight, element.id ? NODE_TOOLTIP_WIDTH : LINK_TOOLTIP_WIDTH, element.id && !element.page ? profileTooltipHeight : tooltipHeight));
  }, []);

  const handleMouseLeave = useCallback((event) => {
    setCurrentActives([])
    setCurrentNode(null);
    setCurrentLink(null);
    setMousePosition({});
  }, []);

  return {
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    opacities,
    currentActives,
  }
}

export const useSankey = (width, height, data) => {
  const sankey = useMemo(() => d3sankey()
    .nodeAlign((node, n) =>
      node.page == 0 ? sankeyRight(node, n) : sankeyLeft(node, n)
    )
    .nodeWidth(10)
    .nodePadding(15)
    .size([width, height]), [height, width]);

  const { nodeLinks, colorScheme } = useSankeyData(data);

  return { sankey, nodeLinks, colorScheme }
}

export const useSankeyContext = () => useContext(SankeyContext)

export function useSankeyData(data) {
  const [nodes, setNodes] = useState([])
  const [links, setLinks] = useState([])
  const [nodeLinks, setNodeLinks] = useState({ nodes, links })
  const colorScheme = useSankeyColor(nodes)

  useEffect(() => {
    const { nodes, links } = data
    setNodes(nodes)
    setLinks(links)
  }, [data])

  useEffect(() => {
    setNodeLinks({ nodes, links })
  }, [nodes, links])

  return useMemo(() => ({ nodeLinks, colorScheme }), [nodeLinks, colorScheme])
}
